import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { VerificationOfMortgagePurpose, VerificationOfMortgageRequestDocument } from '@willow/graphql-iso/src/portal';
import {
  Button,
  ControlledCheckbox,
  ControlledInput,
  ControlledSelect,
  ControlledStateSelect,
  NamedMemo,
  useZodHookForm,
  z,
} from '@willow/shared-web';
import { Form } from '@willow/shared-web/bootstrap';
import { DEFAULT_COUNTRY, StrictAddress, VERIFICATION_OF_MORTGAGE_DAYS } from '@willow/types-iso';

import { PortalSelectedLoan } from '../../App';
import { WillowModal } from '../modal/Modal';
import { ManageLoanHeader } from './Header';

import s from './RequestVOMForm.module.scss';

interface Props {
  loan: PortalSelectedLoan;
}

// Note: if changes are made to this form, check the app version
const FormShape = z
  .object({
    purpose: z.enum(VerificationOfMortgagePurpose),
    purposeOther: z.string().optional(),
    name: z.string(),
    companyName: z.string(),
    address: StrictAddress,
    acceptTerms: z.boolean().refine((val) => val, {
      error: 'The terms of authorization must be accepted before submitting the request',
    }),
  })
  .superRefine((data, ctx) => {
    const { purpose, purposeOther } = data;
    if (purpose === VerificationOfMortgagePurpose.Other && isEmpty(purposeOther)) {
      ctx.addIssue({
        code: 'custom',
        message: `Please describe the purpose of verification`,
        path: ['purposeOther'],
      });
    }
  });

const PURPOSE_OPTIONS = [
  { value: VerificationOfMortgagePurpose.Refinance, label: 'Refinancing' },
  { value: VerificationOfMortgagePurpose.NewLoanApplication, label: 'New Loan Application' },
  { value: VerificationOfMortgagePurpose.Other, label: 'Other' },
];

export const RequestVOMForm = NamedMemo<Props>('RequestVOMForm', ({ loan }) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [verificationOfMortgageRequest] = useMutation(VerificationOfMortgageRequestDocument);

  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    handleSubmit,
    reset,
  } = useZodHookForm(FormShape, {
    defaultValues: {
      purposeOther: undefined,
      name: '',
      companyName: '',
      address: {
        line1: '',
        line2: '',
        line3: '',
        line4: '',
        locality: '',
        region: '',
        postcode: '',
        country: DEFAULT_COUNTRY,
      },
      acceptTerms: false,
    },
  });

  const selectedPurpose = watch('purpose');

  const onSubmit = handleSubmit(async (data) => {
    try {
      await verificationOfMortgageRequest({
        variables: {
          companyId: loan.company.id,
          loanId: loan.id,
          input: {
            purpose: selectedPurpose,
            otherPurpose: selectedPurpose === VerificationOfMortgagePurpose.Other ? data.purposeOther : undefined,
            recipientName: data.name,
            companyName: data.companyName,
            recipientAddress: data.address,
          },
        },
      });

      setShowSuccessModal(true);
      reset();
    } catch {
      toast.error(`Something went wrong submitting the request. Please try again.`);
    }
  });

  return (
    <>
      <ManageLoanHeader
        title="Request a Verification of Mortgage (VOM)"
        subtitle="A verification of mortgage (VOM) is an official statement that verifies your existing loan terms and provides a rating of the payment history, including if the loan was current or delinquent for each month listed. It reflects only the most recent 12 months of your loan, excluding the current month."
      />

      <Form onSubmit={onSubmit}>
        <ControlledSelect
          options={PURPOSE_OPTIONS}
          control={control}
          fieldName="purpose"
          label="Purpose of Verification"
          placeholder="Select purpose"
        />

        {selectedPurpose === VerificationOfMortgagePurpose.Other && (
          <div className="mt-3">
            <ControlledInput control={control} fieldName="purposeOther" placeholder="Purpose of Verification" />
          </div>
        )}

        <div className="mt-3">
          <ControlledInput
            control={control}
            fieldName="name"
            label="VOM Recipient Name"
            placeholder="Name of recipient"
          />
        </div>

        <div className="mt-3">
          <ControlledInput
            control={control}
            fieldName="companyName"
            label="VOM Company Name"
            placeholder="Name of company"
          />
        </div>

        <div className="mt-3">
          <ControlledInput
            control={control}
            fieldName="address.line1"
            label="Recipient Address"
            placeholder="Recipient address"
          />
        </div>
        <div className="mt-3">
          <ControlledInput
            control={control}
            fieldName="address.line2"
            label="Recipient Address Line 2"
            placeholder="Suite 100"
          />
        </div>
        <div className="d-flex flex-wrap justify-content-between">
          <div className={classNames(s.fullWidthAtMd, 'mt-3')}>
            <ControlledInput control={control} fieldName="address.locality" label="City" placeholder="City" />
          </div>
          <div className={classNames(s.fullWidthAtMd, 'mt-3')}>
            <ControlledStateSelect control={control} fieldName="address.region" label="State" />
          </div>
          <div className={classNames(s.fullWidthAtMd, 'mt-3')}>
            <ControlledInput control={control} fieldName="address.postcode" label="Zip Code" placeholder="Zip code" />
          </div>
        </div>

        <div
          className={classNames('mt-4 u-border-1 p-2 u-fs-2', {
            'u-border-color-bark1': !errors.acceptTerms,
            'u-border-color-red1': errors.acceptTerms,
          })}
        >
          <ControlledCheckbox
            control={control}
            fieldName="acceptTerms"
            label={`I authorize ${loan.company.name} to provide the following information regarding loan ${loan.losId} to the authorized party listed above.`}
          />
        </div>

        <div className="mt-4 u-fs-2">
          Please allow up to <b>{VERIFICATION_OF_MORTGAGE_DAYS} business days</b> from your request to generate your VOM
          statement. Once created, a copy will be made available for download in your "documents" page.
        </div>

        <div className="mt-4 w-100 d-flex justify-content-end">
          <Button type="submit" className="mt-3" variant="primary" size="md" onClick={onSubmit} loading={isSubmitting}>
            Submit Request
          </Button>
        </div>
      </Form>

      <WillowModal showModal={showSuccessModal} onClose={() => setShowSuccessModal(false)}>
        <h2 className="u-fs-4 u-bold mb-2">Verification of Mortgage Requested</h2>
        <div>
          {loan.company.name} is generating your request. Once created, a copy will be made available for download in
          your “Documents” page.
        </div>
        <div className="mt-4 ms-auto">
          <Button
            variant="primary"
            size="sm"
            className="hover-override hover:u-bg-secondary"
            onClick={() => setShowSuccessModal(false)}
          >
            Close
          </Button>
        </div>
      </WillowModal>
    </>
  );
});
